// Character info for Hebrew and Greek analysis

export interface CharacterInfo {
    name: string;
    type: 'consonant' | 'vowel' | 'cantillation' | 'punctuation' | 'accent' | 'letter' | 'breathing' | 'other';
    phonetic?: string;
    translit?: string;  // SBL transliteration
    description?: string;
}

// Hebrew character mappings (SBL transliteration standard)
export const hebrewCharInfo: Record<string, CharacterInfo> = {
    // Consonants - phonetic is simple sound for quick pronunciation
    'א': { name: 'Aleph', type: 'consonant', phonetic: '', translit: 'ʾ', description: 'First letter' },
    'ב': { name: 'Bet', type: 'consonant', phonetic: 'b', translit: 'b', description: 'Second letter' },
    'ג': { name: 'Gimel', type: 'consonant', phonetic: 'g', translit: 'g', description: 'Third letter' },
    'ד': { name: 'Dalet', type: 'consonant', phonetic: 'd', translit: 'd', description: 'Fourth letter' },
    'ה': { name: 'He', type: 'consonant', phonetic: 'h', translit: 'h', description: 'Fifth letter' },
    'ו': { name: 'Vav', type: 'consonant', phonetic: 'v', translit: 'w', description: 'Sixth letter' },
    'ז': { name: 'Zayin', type: 'consonant', phonetic: 'z', translit: 'z', description: 'Seventh letter' },
    'ח': { name: 'Chet', type: 'consonant', phonetic: 'ch', translit: 'ḥ', description: 'Eighth letter, guttural' },
    'ט': { name: 'Tet', type: 'consonant', phonetic: 't', translit: 'ṭ', description: 'Ninth letter' },
    'י': { name: 'Yod', type: 'consonant', phonetic: 'y', translit: 'y', description: 'Tenth letter' },
    'כ': { name: 'Kaf', type: 'consonant', phonetic: 'k', translit: 'k', description: 'Eleventh letter' },
    'ך': { name: 'Final Kaf', type: 'consonant', phonetic: 'k', translit: 'k', description: 'Kaf at end of word' },
    'ל': { name: 'Lamed', type: 'consonant', phonetic: 'l', translit: 'l', description: 'Twelfth letter' },
    'מ': { name: 'Mem', type: 'consonant', phonetic: 'm', translit: 'm', description: 'Thirteenth letter' },
    'ם': { name: 'Final Mem', type: 'consonant', phonetic: 'm', translit: 'm', description: 'Mem at end of word' },
    'נ': { name: 'Nun', type: 'consonant', phonetic: 'n', translit: 'n', description: 'Fourteenth letter' },
    'ן': { name: 'Final Nun', type: 'consonant', phonetic: 'n', translit: 'n', description: 'Nun at end of word' },
    'ס': { name: 'Samekh', type: 'consonant', phonetic: 's', translit: 's', description: 'Fifteenth letter' },
    'ע': { name: 'Ayin', type: 'consonant', phonetic: '', translit: 'ʿ', description: 'Sixteenth letter' },
    'פ': { name: 'Pe', type: 'consonant', phonetic: 'p', translit: 'p', description: 'Seventeenth letter' },
    'ף': { name: 'Final Pe', type: 'consonant', phonetic: 'f', translit: 'p', description: 'Pe at end of word' },
    'צ': { name: 'Tsade', type: 'consonant', phonetic: 'ts', translit: 'ṣ', description: 'Eighteenth letter' },
    'ץ': { name: 'Final Tsade', type: 'consonant', phonetic: 'ts', translit: 'ṣ', description: 'Tsade at end of word' },
    'ק': { name: 'Qof', type: 'consonant', phonetic: 'k', translit: 'q', description: 'Nineteenth letter' },
    'ר': { name: 'Resh', type: 'consonant', phonetic: 'r', translit: 'r', description: 'Twentieth letter' },
    'ש': { name: 'Shin/Sin', type: 'consonant', phonetic: 'sh', translit: 'š', description: 'Twenty-first letter' },
    'ת': { name: 'Tav', type: 'consonant', phonetic: 't', translit: 't', description: 'Twenty-second letter' },
    // Vowel points (nikkud) - phonetic is simple English sound
    '\u05B0': { name: 'Sheva', type: 'vowel', phonetic: 'uh', translit: 'ĕ', description: 'Two vertical dots' },
    '\u05B1': { name: 'Hataf Segol', type: 'vowel', phonetic: 'eh', translit: 'ĕ', description: 'Reduced segol' },
    '\u05B2': { name: 'Hataf Patach', type: 'vowel', phonetic: 'ah', translit: 'ă', description: 'Reduced patach' },
    '\u05B3': { name: 'Hataf Qamats', type: 'vowel', phonetic: 'oh', translit: 'ŏ', description: 'Reduced qamats' },
    '\u05B4': { name: 'Hiriq', type: 'vowel', phonetic: 'ee', translit: 'i', description: 'Single dot below' },
    '\u05B5': { name: 'Tsere', type: 'vowel', phonetic: 'ay', translit: 'ē', description: 'Two horizontal dots' },
    '\u05B6': { name: 'Segol', type: 'vowel', phonetic: 'eh', translit: 'e', description: 'Three dots (triangle)' },
    '\u05B7': { name: 'Patach', type: 'vowel', phonetic: 'ah', translit: 'a', description: 'Horizontal line below' },
    '\u05B8': { name: 'Qamats', type: 'vowel', phonetic: 'ah', translit: 'ā', description: 'T-shape below' },
    '\u05B9': { name: 'Holam', type: 'vowel', phonetic: 'oh', translit: 'ō', description: 'Dot above left' },
    '\u05BA': { name: 'Holam Haser', type: 'vowel', phonetic: 'oh', translit: 'ō', description: 'Holam on vav' },
    '\u05BB': { name: 'Qubuts', type: 'vowel', phonetic: 'oo', translit: 'u', description: 'Three diagonal dots' },
    '\u05BC': { name: 'Dagesh', type: 'other' },
    '\u05BD': { name: 'Meteg', type: 'other' },
    '\u05BE': { name: 'Maqaf', type: 'punctuation', translit: '-' },
    '\u05BF': { name: 'Rafe', type: 'other' },
    '\u05C1': { name: 'Shin Dot', type: 'other' },
    '\u05C2': { name: 'Sin Dot', type: 'other' },
    '\u05C3': { name: 'Sof Pasuq', type: 'punctuation' },
    // Cantillation marks (te'amim) - no phonetic, these are for chanting
    '\u0591': { name: 'Etnahta', type: 'cantillation' },
    '\u0592': { name: 'Segol (accent)', type: 'cantillation' },
    '\u0593': { name: 'Shalshelet', type: 'cantillation' },
    '\u0594': { name: 'Zaqef Qatan', type: 'cantillation' },
    '\u0595': { name: 'Zaqef Gadol', type: 'cantillation' },
    '\u0596': { name: 'Tipeha', type: 'cantillation' },
    '\u0597': { name: 'Revia', type: 'cantillation' },
    '\u0598': { name: 'Zarqa', type: 'cantillation' },
    '\u0599': { name: 'Pashta', type: 'cantillation' },
    '\u059A': { name: 'Yetiv', type: 'cantillation' },
    '\u059B': { name: 'Tevir', type: 'cantillation' },
    '\u059C': { name: 'Geresh', type: 'cantillation' },
    '\u059D': { name: 'Geresh Muqdam', type: 'cantillation' },
    '\u059E': { name: 'Gershayim', type: 'cantillation' },
    '\u059F': { name: 'Qarney Para', type: 'cantillation' },
    '\u05A0': { name: 'Telisha Gedola', type: 'cantillation' },
    '\u05A1': { name: 'Pazer', type: 'cantillation' },
    '\u05A3': { name: 'Munah', type: 'cantillation' },
    '\u05A4': { name: 'Mahapakh', type: 'cantillation' },
    '\u05A5': { name: 'Merkha', type: 'cantillation' },
    '\u05A6': { name: 'Merkha Kefula', type: 'cantillation' },
    '\u05A7': { name: 'Darga', type: 'cantillation' },
    '\u05A8': { name: 'Qadma', type: 'cantillation' },
    '\u05A9': { name: 'Telisha Qetana', type: 'cantillation' },
    '\u05AA': { name: 'Yerah Ben Yomo', type: 'cantillation' },
    '\u05AB': { name: 'Ole', type: 'cantillation' },
    '\u05AC': { name: 'Iluy', type: 'cantillation' },
    '\u05AD': { name: 'Dehi', type: 'cantillation' },
    '\u05AE': { name: 'Zinor', type: 'cantillation' },
    '\u05C0': { name: 'Paseq', type: 'punctuation' },
};

// Greek character mappings - phonetic is simple sound for quick pronunciation
export const greekCharInfo: Record<string, CharacterInfo> = {
    // Lowercase
    'α': { name: 'Alpha', type: 'letter', phonetic: 'ah', translit: 'a' },
    'β': { name: 'Beta', type: 'letter', phonetic: 'b', translit: 'b' },
    'γ': { name: 'Gamma', type: 'letter', phonetic: 'g', translit: 'g' },
    'δ': { name: 'Delta', type: 'letter', phonetic: 'd', translit: 'd' },
    'ε': { name: 'Epsilon', type: 'letter', phonetic: 'eh', translit: 'e' },
    'ζ': { name: 'Zeta', type: 'letter', phonetic: 'z', translit: 'z' },
    'η': { name: 'Eta', type: 'letter', phonetic: 'ay', translit: 'ē' },
    'θ': { name: 'Theta', type: 'letter', phonetic: 'th', translit: 'th' },
    'ι': { name: 'Iota', type: 'letter', phonetic: 'ee', translit: 'i' },
    'κ': { name: 'Kappa', type: 'letter', phonetic: 'k', translit: 'k' },
    'λ': { name: 'Lambda', type: 'letter', phonetic: 'l', translit: 'l' },
    'μ': { name: 'Mu', type: 'letter', phonetic: 'm', translit: 'm' },
    'ν': { name: 'Nu', type: 'letter', phonetic: 'n', translit: 'n' },
    'ξ': { name: 'Xi', type: 'letter', phonetic: 'ks', translit: 'x' },
    'ο': { name: 'Omicron', type: 'letter', phonetic: 'oh', translit: 'o' },
    'π': { name: 'Pi', type: 'letter', phonetic: 'p', translit: 'p' },
    'ρ': { name: 'Rho', type: 'letter', phonetic: 'r', translit: 'r' },
    'σ': { name: 'Sigma', type: 'letter', phonetic: 's', translit: 's' },
    'ς': { name: 'Final Sigma', type: 'letter', phonetic: 's', translit: 's' },
    'τ': { name: 'Tau', type: 'letter', phonetic: 't', translit: 't' },
    'υ': { name: 'Upsilon', type: 'letter', phonetic: 'oo', translit: 'y' },
    'φ': { name: 'Phi', type: 'letter', phonetic: 'f', translit: 'ph' },
    'χ': { name: 'Chi', type: 'letter', phonetic: 'ch', translit: 'ch' },
    'ψ': { name: 'Psi', type: 'letter', phonetic: 'ps', translit: 'ps' },
    'ω': { name: 'Omega', type: 'letter', phonetic: 'oh', translit: 'ō' },
    // Uppercase
    'Α': { name: 'Alpha', type: 'letter', phonetic: 'ah', translit: 'A' },
    'Β': { name: 'Beta', type: 'letter', phonetic: 'b', translit: 'B' },
    'Γ': { name: 'Gamma', type: 'letter', phonetic: 'g', translit: 'G' },
    'Δ': { name: 'Delta', type: 'letter', phonetic: 'd', translit: 'D' },
    'Ε': { name: 'Epsilon', type: 'letter', phonetic: 'eh', translit: 'E' },
    'Ζ': { name: 'Zeta', type: 'letter', phonetic: 'z', translit: 'Z' },
    'Η': { name: 'Eta', type: 'letter', phonetic: 'ay', translit: 'Ē' },
    'Θ': { name: 'Theta', type: 'letter', phonetic: 'th', translit: 'Th' },
    'Ι': { name: 'Iota', type: 'letter', phonetic: 'ee', translit: 'I' },
    'Κ': { name: 'Kappa', type: 'letter', phonetic: 'k', translit: 'K' },
    'Λ': { name: 'Lambda', type: 'letter', phonetic: 'l', translit: 'L' },
    'Μ': { name: 'Mu', type: 'letter', phonetic: 'm', translit: 'M' },
    'Ν': { name: 'Nu', type: 'letter', phonetic: 'n', translit: 'N' },
    'Ξ': { name: 'Xi', type: 'letter', phonetic: 'ks', translit: 'X' },
    'Ο': { name: 'Omicron', type: 'letter', phonetic: 'oh', translit: 'O' },
    'Π': { name: 'Pi', type: 'letter', phonetic: 'p', translit: 'P' },
    'Ρ': { name: 'Rho', type: 'letter', phonetic: 'r', translit: 'R' },
    'Σ': { name: 'Sigma', type: 'letter', phonetic: 's', translit: 'S' },
    'Τ': { name: 'Tau', type: 'letter', phonetic: 't', translit: 'T' },
    'Υ': { name: 'Upsilon', type: 'letter', phonetic: 'oo', translit: 'Y' },
    'Φ': { name: 'Phi', type: 'letter', phonetic: 'f', translit: 'Ph' },
    'Χ': { name: 'Chi', type: 'letter', phonetic: 'ch', translit: 'Ch' },
    'Ψ': { name: 'Psi', type: 'letter', phonetic: 'ps', translit: 'Ps' },
    'Ω': { name: 'Omega', type: 'letter', phonetic: 'oh', translit: 'Ō' },
    // Combining diacriticals - no phonetic contribution
    '\u0300': { name: 'Grave Accent', type: 'accent', phonetic: '', translit: '' },
    '\u0301': { name: 'Acute Accent', type: 'accent', phonetic: '', translit: '' },
    '\u0302': { name: 'Circumflex', type: 'accent', phonetic: '', translit: '' },
    '\u0308': { name: 'Diaeresis', type: 'accent', phonetic: '', translit: '' },
    '\u0313': { name: 'Smooth Breathing', type: 'breathing', phonetic: '', translit: '' },
    '\u0314': { name: 'Rough Breathing', type: 'breathing', phonetic: 'h', translit: 'h' },
    '\u0342': { name: 'Perispomeni', type: 'accent', phonetic: '', translit: '' },
    '\u0345': { name: 'Iota Subscript', type: 'other', phonetic: '', translit: '' },
    // Common precomposed
    'ά': { name: 'Alpha + Acute', type: 'letter', phonetic: 'stressed "a"' },
    'έ': { name: 'Epsilon + Acute', type: 'letter', phonetic: 'stressed "e"' },
    'ή': { name: 'Eta + Acute', type: 'letter', phonetic: 'stressed long "e"' },
    'ί': { name: 'Iota + Acute', type: 'letter', phonetic: 'stressed "i"' },
    'ό': { name: 'Omicron + Acute', type: 'letter', phonetic: 'stressed "o"' },
    'ύ': { name: 'Upsilon + Acute', type: 'letter', phonetic: 'stressed "u"' },
    'ώ': { name: 'Omega + Acute', type: 'letter', phonetic: 'stressed long "o"' },
    'ὰ': { name: 'Alpha + Grave', type: 'letter', phonetic: '"a" (unstressed)' },
    'ὲ': { name: 'Epsilon + Grave', type: 'letter', phonetic: '"e" (unstressed)' },
    'ὴ': { name: 'Eta + Grave', type: 'letter', phonetic: 'long "e" (unstressed)' },
    'ὶ': { name: 'Iota + Grave', type: 'letter', phonetic: '"i" (unstressed)' },
    'ὸ': { name: 'Omicron + Grave', type: 'letter', phonetic: '"o" (unstressed)' },
    'ὺ': { name: 'Upsilon + Grave', type: 'letter', phonetic: '"u" (unstressed)' },
    'ὼ': { name: 'Omega + Grave', type: 'letter', phonetic: 'long "o" (unstressed)' },
    'ᾶ': { name: 'Alpha + Circumflex', type: 'letter', phonetic: '"a" (circumflex)' },
    'ῆ': { name: 'Eta + Circumflex', type: 'letter', phonetic: 'long "e" (circumflex)' },
    'ῖ': { name: 'Iota + Circumflex', type: 'letter', phonetic: '"i" (circumflex)' },
    'ῦ': { name: 'Upsilon + Circumflex', type: 'letter', phonetic: '"u" (circumflex)' },
    'ῶ': { name: 'Omega + Circumflex', type: 'letter', phonetic: 'long "o" (circumflex)' },
    'ἀ': { name: 'Alpha + Smooth', type: 'letter', phonetic: '"a" (no h)' },
    'ἁ': { name: 'Alpha + Rough', type: 'letter', phonetic: '"ha"' },
    'ἐ': { name: 'Epsilon + Smooth', type: 'letter', phonetic: '"e" (no h)' },
    'ἑ': { name: 'Epsilon + Rough', type: 'letter', phonetic: '"he"' },
    'ἠ': { name: 'Eta + Smooth', type: 'letter', phonetic: 'long "e" (no h)' },
    'ἡ': { name: 'Eta + Rough', type: 'letter', phonetic: '"he" (long)' },
    'ἰ': { name: 'Iota + Smooth', type: 'letter', phonetic: '"i" (no h)' },
    'ἱ': { name: 'Iota + Rough', type: 'letter', phonetic: '"hi"' },
    'ὀ': { name: 'Omicron + Smooth', type: 'letter', phonetic: '"o" (no h)' },
    'ὁ': { name: 'Omicron + Rough', type: 'letter', phonetic: '"ho"' },
    'ὐ': { name: 'Upsilon + Smooth', type: 'letter', phonetic: '"u" (no h)' },
    'ὑ': { name: 'Upsilon + Rough', type: 'letter', phonetic: '"hu"' },
    'ὠ': { name: 'Omega + Smooth', type: 'letter', phonetic: 'long "o" (no h)' },
    'ὡ': { name: 'Omega + Rough', type: 'letter', phonetic: '"ho" (long)' },
    'ῥ': { name: 'Rho + Rough', type: 'letter', phonetic: '"rh"' },
    'ἄ': { name: 'Alpha + Smooth + Acute', type: 'letter', phonetic: 'stressed "a"' },
    'ἅ': { name: 'Alpha + Rough + Acute', type: 'letter', phonetic: 'stressed "ha"' },
    'ἔ': { name: 'Epsilon + Smooth + Acute', type: 'letter', phonetic: 'stressed "e"' },
    'ἕ': { name: 'Epsilon + Rough + Acute', type: 'letter', phonetic: 'stressed "he"' },
    'ἤ': { name: 'Eta + Smooth + Acute', type: 'letter', phonetic: 'stressed long "e"' },
    'ἥ': { name: 'Eta + Rough + Acute', type: 'letter', phonetic: 'stressed "he"' },
    'ἴ': { name: 'Iota + Smooth + Acute', type: 'letter', phonetic: 'stressed "i"' },
    'ἵ': { name: 'Iota + Rough + Acute', type: 'letter', phonetic: 'stressed "hi"' },
    'ὄ': { name: 'Omicron + Smooth + Acute', type: 'letter', phonetic: 'stressed "o"' },
    'ὅ': { name: 'Omicron + Rough + Acute', type: 'letter', phonetic: 'stressed "ho"' },
    'ὔ': { name: 'Upsilon + Smooth + Acute', type: 'letter', phonetic: 'stressed "u"' },
    'ὕ': { name: 'Upsilon + Rough + Acute', type: 'letter', phonetic: 'stressed "hu"' },
    'ὤ': { name: 'Omega + Smooth + Acute', type: 'letter', phonetic: 'stressed long "o"' },
    'ὥ': { name: 'Omega + Rough + Acute', type: 'letter', phonetic: 'stressed "ho"' },
    'ᾳ': { name: 'Alpha + Iota Subscript', type: 'letter', phonetic: '"a" (iota silent)' },
    'ῃ': { name: 'Eta + Iota Subscript', type: 'letter', phonetic: 'long "e" (iota silent)' },
    'ῳ': { name: 'Omega + Iota Subscript', type: 'letter', phonetic: 'long "o" (iota silent)' },
    '·': { name: 'Ano Teleia', type: 'punctuation', phonetic: 'like semicolon' },
    ';': { name: 'Question Mark', type: 'punctuation', phonetic: 'question' },
    '\u2019': { name: 'Apostrophe', type: 'punctuation', phonetic: 'elision' },
};

// Parsed character with info - flattened for easy use
export interface ParsedCharacter {
    char: string;
    name: string;
    type: CharacterInfo['type'];
    phonetic?: string;
    translit?: string;
}

// Parse word into characters with info
export const parseWordCharacters = (word: string, lang: 'hebrew' | 'greek'): ParsedCharacter[] => {
    const charMap = lang === 'hebrew' ? hebrewCharInfo : greekCharInfo;
    const normalized = word.normalize('NFD');
    const result: ParsedCharacter[] = [];
    // Characters to exclude from breakdown (punctuation that isn't linguistic)
    const excludeChars = [',', '.', ';', ':', '!', '?', '·'];
    for (const char of normalized) {
        if (excludeChars.includes(char)) continue;
        const info = charMap[char];
        result.push({
            char,
            name: info?.name || `Unknown (U+${char.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')})`,
            type: info?.type || 'other',
            phonetic: info?.phonetic,
            translit: info?.translit
        });
    }
    return result;
};

// Get full transliteration of a word
export const getTransliteration = (word: string, lang: 'hebrew' | 'greek'): string => {
    const chars = parseWordCharacters(word, lang);
    return chars
        .map(c => c.translit || '')
        .join('');
};

// Color for character type
export const getTypeColor = (type: CharacterInfo['type']): string => {
    const colors: Record<string, string> = {
        consonant: '#1a365d', letter: '#1a365d',
        vowel: '#c53030',
        cantillation: '#2f855a',
        accent: '#805ad5',
        breathing: '#d69e2e',
        punctuation: '#718096',
        other: '#4a5568'
    };
    return colors[type] || '#4a5568';
};

// Type label for display
export const getTypeLabel = (type: CharacterInfo['type']): string => {
    const labels: Record<string, string> = {
        consonant: 'Consonant',
        letter: 'Letter',
        vowel: 'Vowel',
        cantillation: 'Cantillation',
        accent: 'Accent',
        breathing: 'Breathing',
        punctuation: 'Punctuation',
        other: 'Other'
    };
    return labels[type] || 'Unknown';
};
